import { useEffect, useState } from 'react';
import DataTable from '../components/DataTable';
import ErrorMessage from '../components/ErrorMessage';
import LoadingState from '../components/LoadingState';
import { useActiveUpload } from '../contexts/ActiveUploadContext';
import { useAuth } from '../contexts/AuthContext';
import { activateUpload, cleanupOldUploads, deleteUpload, getUploads } from '../services/uploadService';
import { safeDateLabel } from '../utils/formatters';

export default function UploadHistoryPage() {
  const { isAdmin } = useAuth();
  const { refreshActiveUpload } = useActiveUpload();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cleaning, setCleaning] = useState(false);
  const [deletingUploadId, setDeletingUploadId] = useState('');

  async function loadUploads() {
    setLoading(true);
    setError('');
    try {
      const data = await getUploads();
      setUploads(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadUploads(); }, []);

  async function handleActivate(uploadId) {
    setError('');
    setSuccess('');
    try {
      await activateUpload(uploadId);
      await Promise.all([loadUploads(), refreshActiveUpload()]);
      setSuccess('Upload activated. Dashboard data source updated.');
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(upload) {
    setError('');
    setSuccess('');

    if (upload.is_active) {
      setError('The active upload cannot be deleted. Activate another completed upload first, then delete this one.');
      return;
    }

    const confirmed = window.confirm(
      `Delete this uploaded report and all related dashboard rows?\n\n${upload.file_name}\n\nThis cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingUploadId(upload.id);
    try {
      await deleteUpload(upload.id);
      await Promise.all([loadUploads(), refreshActiveUpload()]);
      setSuccess(`Deleted upload: ${upload.file_name}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingUploadId('');
    }
  }

  async function handleCleanup() {
    setError('');
    setSuccess('');
    setCleaning(true);
    try {
      const result = await cleanupOldUploads();
      await Promise.all([loadUploads(), refreshActiveUpload()]);
      const deleted = Number(result?.deleted_uploads || 0);
      const retained = Number(result?.retained_uploads || 0);
      setSuccess(`Cleanup complete. Deleted ${deleted} old upload${deleted === 1 ? '' : 's'} and retained ${retained}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setCleaning(false);
    }
  }

  const rows = uploads.map((upload) => ({
    ...upload,
    uploaded_at_label: safeDateLabel(upload.uploaded_at),
    date_range: upload.min_milestone_date && upload.max_milestone_date ? `${upload.min_milestone_date} to ${upload.max_milestone_date}` : '—',
    active_label: upload.is_active ? 'Active' : 'Inactive',
  }));

  return (
    <div className="page-stack">
      <div className="page-title-row">
        <div>
          <h2>Upload History</h2>
          <p>Review uploaded reports, activate a dashboard source, or delete old reports to reduce Supabase database size.</p>
        </div>
        {isAdmin && (
          <button type="button" className="button secondary" onClick={handleCleanup} disabled={cleaning}>
            {cleaning ? 'Cleaning...' : 'Clean Old Uploads'}
          </button>
        )}
      </div>
      <ErrorMessage message={error} />
      {success && <div className="success-message">{success}</div>}
      {loading ? <LoadingState message="Loading upload history..." /> : (
        <div className="panel">
          <DataTable
            columns={[
              { key: 'file_name', label: 'File Name' },
              { key: 'uploaded_at_label', label: 'Upload Date' },
              { key: 'uploaded_by_email', label: 'Uploaded By' },
              { key: 'status', label: 'Status' },
              { key: 'active_label', label: 'Active' },
              { key: 'valid_rows', label: 'Valid Rows', align: 'right' },
              { key: 'skipped_rows', label: 'Skipped', align: 'right' },
              { key: 'date_range', label: 'Date Range' },
              {
                key: 'id',
                label: 'Action',
                render: (_value, row) => {
                  if (!isAdmin) return '—';
                  return (
                    <div className="table-action-row">
                      {row.status === 'completed' && !row.is_active ? (
                        <button type="button" className="button small" onClick={() => handleActivate(row.id)}>
                          Activate
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="button small danger"
                        onClick={() => handleDelete(row)}
                        disabled={row.is_active || deletingUploadId === row.id}
                        title={row.is_active ? 'Activate another upload before deleting this active report.' : 'Delete this upload and all related rows.'}
                      >
                        {deletingUploadId === row.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  );
                },
              },
            ]}
            rows={rows}
          />
        </div>
      )}
    </div>
  );
}
